import { getJson, getList, send } from './client';
import { getSiteIntegrations } from './siteIntegrations';
import type {
  AnalyticsAnnotation,
  AnalyticsFunnelStep,
  AnalyticsGeoStatus,
  AnalyticsHeatmap,
  AnalyticsLive,
  AnalyticsOverview,
  AnalyticsSession,
  AnalyticsSessionDetail,
  AnalyticsSite,
  AnalyticsTop,
  AnalyticsTrafficTable,
  AnalyticsSeriesPoint,
  DashboardCard,
} from './types';

/**
 * The analytics API.
 *
 * `range` is CIVIL DATES in the tenant's timezone, never instants — the server
 * buckets on civil days, and handing it an instant asks a question the rollups
 * cannot answer.
 */
export interface AnalyticsRange {
  siteId?: number;
  from?: string;
  to?: string;
  compare?: 'previous' | 'year';
}

const query = (params: Record<string, string | number | undefined | null>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

const rangeQuery = (range: AnalyticsRange, extra: Record<string, string | number | undefined> = {}) =>
  query({ site_id: range.siteId, from: range.from, to: range.to, compare: range.compare, ...extra });

export const listAnalyticsSites = () => getList<AnalyticsSite>('/analytics/sites');

export const createAnalyticsSite = (body: { name: string; platform?: 'web' | 'app' }) =>
  send<{ site: AnalyticsSite }>('POST', '/analytics/sites', body);

export const updateAnalyticsSite = (id: number, body: Partial<AnalyticsSite>) =>
  send<{ site: AnalyticsSite }>('PATCH', `/analytics/sites/${id}`, body);

export const deleteAnalyticsSite = (id: number) =>
  send<{ deleted: boolean }>('DELETE', `/analytics/sites/${id}`);

/** The subject-access erasure path. */
export const purgeAnalyticsVisitor = (id: number, visitorKey: string) =>
  send<{ deleted: boolean }>('POST', `/analytics/sites/${id}/purge_visitor`, { visitorKey });

export const getAnalyticsOverview = (range: AnalyticsRange) =>
  getJson<AnalyticsOverview>(`/analytics/overview${rangeQuery(range)}`);

export const getAnalyticsSeries = (
  range: AnalyticsRange,
  metric: 'sessions' | 'pageviews' | 'visitors' | 'bookings',
) => getList<AnalyticsSeriesPoint>(`/analytics/timeseries${rangeQuery(range, { metric })}`);

/**
 * One endpoint, sixteen cards — see DIMENSIONS in the controller.
 *
 * `country` narrows `region` and `city` to one country and is ignored on every
 * other dimension. It is answered from the visits rather than the rollups, so
 * it only reaches back as far as the raw window.
 */
export const getAnalyticsTop = (range: AnalyticsRange, dim: string, limit = 10, country?: string) =>
  getJson<AnalyticsTop>(`/analytics/top${rangeQuery(range, { dim, limit, country })}`);

export const getAnalyticsFunnel = (range: AnalyticsRange) =>
  getList<AnalyticsFunnelStep>(`/analytics/funnel${rangeQuery(range)}`);

export const getAnalyticsTrafficTable = (range: AnalyticsRange) =>
  getJson<AnalyticsTrafficTable>(`/analytics/traffic_table${rangeQuery(range)}`);

export const getAnalyticsLive = (siteId?: number) =>
  getJson<AnalyticsLive>(`/analytics/live${query({ site_id: siteId })}`);

export const listAnalyticsSessions = (
  range: AnalyticsRange,
  filters: {
    page?: number;
    /** Only `'true'` narrows — the server has no anonymous-only filter. */
    identified?: string;
    outcome?: string;
    country?: string;
    device?: string;
    channel?: string;
  } = {},
  // `pageSize` is optional because a preview fixture need not page; a caller
  // that must know how far the list runs falls back to the payload length.
) => getJson<{ stats: { total: number; page: number; pageSize?: number }; payload: AnalyticsSession[] }>(
  `/analytics/sessions${rangeQuery(range, filters)}`,
);

export const getAnalyticsSession = (key: string, siteId?: number) =>
  getJson<AnalyticsSessionDetail>(`/analytics/sessions/${encodeURIComponent(key)}${query({ site_id: siteId })}`);

export const getAnalyticsHeatmap = (range: AnalyticsRange, path: string, device: string) =>
  getJson<AnalyticsHeatmap>(`/analytics/heatmap${rangeQuery(range, { path, device })}`);

export const getAnalyticsDashboard = (siteId?: number) =>
  getJson<{ cards: DashboardCard[] }>(`/analytics/dashboard${query({ site_id: siteId })}`);

export const saveAnalyticsDashboard = (siteId: number, cards: DashboardCard[]) =>
  send<{ cards: DashboardCard[] }>('PUT', '/analytics/dashboard', { siteId, cards });

/**
 * The location database — one per DEPLOYMENT, not per tenant. Reading its state
 * is a `view`; setting the key is root, because it is shared by every install
 * on this CRM.
 */
export const getAnalyticsGeo = () => getJson<AnalyticsGeoStatus>('/analytics/geoip');

/** An empty string clears the key and falls back to the deployment's env. */
export const saveAnalyticsGeoKey = (licenseKey: string) =>
  send<AnalyticsGeoStatus>('PUT', '/analytics/geoip', { licenseKey });

/** Enqueues the download; the outcome arrives through `getAnalyticsGeo`. */
export const refreshAnalyticsGeo = () =>
  send<{ started: boolean }>('POST', '/analytics/geoip/refresh', {});

export const listAnalyticsAnnotations = (range: AnalyticsRange) =>
  getList<AnalyticsAnnotation>(`/analytics/annotations${rangeQuery(range)}`);

export const createAnalyticsAnnotation = (siteId: number, day: string, label: string) =>
  send<{ annotation: AnalyticsAnnotation }>('POST', '/analytics/annotations', { siteId, day, label });

export const deleteAnalyticsAnnotation = (id: number) =>
  send<{ deleted: boolean }>('DELETE', `/analytics/annotations/${id}`);

/**
 * The slice of the API the analytics screen calls.
 *
 * The screen takes this as a prop so the embedded and preview mounts can hand it
 * `source.api` — the CommsSource seam — while the standalone `/analytics` route
 * uses `analyticsApi` below. Structural, not nominal: `api/comms.ts` re-exports
 * every one of these, so `CommsApi` satisfies it with no adapter, and a preview
 * fixture that forgets one is a compile error rather than a blank card.
 */
export interface AnalyticsApi {
  listAnalyticsSites: typeof listAnalyticsSites;
  createAnalyticsSite: typeof createAnalyticsSite;
  updateAnalyticsSite: typeof updateAnalyticsSite;
  deleteAnalyticsSite: typeof deleteAnalyticsSite;
  purgeAnalyticsVisitor: typeof purgeAnalyticsVisitor;
  getAnalyticsOverview: typeof getAnalyticsOverview;
  getAnalyticsSeries: typeof getAnalyticsSeries;
  getAnalyticsTop: typeof getAnalyticsTop;
  getAnalyticsFunnel: typeof getAnalyticsFunnel;
  getAnalyticsTrafficTable: typeof getAnalyticsTrafficTable;
  getAnalyticsLive: typeof getAnalyticsLive;
  listAnalyticsSessions: typeof listAnalyticsSessions;
  getAnalyticsSession: typeof getAnalyticsSession;
  getAnalyticsHeatmap: typeof getAnalyticsHeatmap;
  getAnalyticsDashboard: typeof getAnalyticsDashboard;
  saveAnalyticsDashboard: typeof saveAnalyticsDashboard;
  getAnalyticsGeo: typeof getAnalyticsGeo;
  saveAnalyticsGeoKey: typeof saveAnalyticsGeoKey;
  refreshAnalyticsGeo: typeof refreshAnalyticsGeo;
  listAnalyticsAnnotations: typeof listAnalyticsAnnotations;
  createAnalyticsAnnotation: typeof createAnalyticsAnnotation;
  deleteAnalyticsAnnotation: typeof deleteAnalyticsAnnotation;
  /** Which install the booking-widget pages report to — read by Install, never written here. */
  getSiteIntegrations: typeof getSiteIntegrations;
}

export const analyticsApi: AnalyticsApi = {
  listAnalyticsSites,
  createAnalyticsSite,
  updateAnalyticsSite,
  deleteAnalyticsSite,
  purgeAnalyticsVisitor,
  getAnalyticsOverview,
  getAnalyticsSeries,
  getAnalyticsTop,
  getAnalyticsFunnel,
  getAnalyticsTrafficTable,
  getAnalyticsLive,
  listAnalyticsSessions,
  getAnalyticsSession,
  getAnalyticsHeatmap,
  getAnalyticsDashboard,
  saveAnalyticsDashboard,
  getAnalyticsGeo,
  saveAnalyticsGeoKey,
  refreshAnalyticsGeo,
  listAnalyticsAnnotations,
  createAnalyticsAnnotation,
  deleteAnalyticsAnnotation,
  getSiteIntegrations,
};
